import {
  DEMO_CAMPAIGN_ID,
  DEMO_CAMPAIGN_SLUG,
  DEMO_USER_SLUG,
} from "@playlive/fundraiser-data/demo";
import { useCallback, useContext, useState } from "react";
import { TiltifyConfigContext } from "@/context/tiltifyConfigContext";
import { isTestMode } from "@/util/const";
import { type ChangeEventLike, restoreFromLocalStorage } from "@/util/localStorageConfig.ts";

export const defaultTiltifyConfig = {
  tiltifyUserID: isTestMode ? DEMO_CAMPAIGN_ID : "",
  tiltifyUsername: isTestMode ? DEMO_USER_SLUG : "",
  tiltifyUserSlug: isTestMode ? DEMO_USER_SLUG : "",
  tiltifyCampaignSlug: isTestMode ? DEMO_CAMPAIGN_SLUG : "",
  tiltifyCampaignID: isTestMode ? DEMO_CAMPAIGN_ID : "",
  tiltifyPollID: isTestMode ? import.meta.env.VITE_TILTIFY_TEST_POLL_ID : "",
  tiltifyCampaignGoal: isTestMode ? "1000" : "0",
  tiltifyCampaignName: isTestMode ? "Test Campaign" : "",
  tiltifyTeamSlug: "",
  tiltifyIsTeamCampaign: false as boolean,
  tiltifyAllowRetired: false as boolean,
  tiltifyAccessToken: "",
  tiltifyRefreshToken: "",
  tiltifyTokenExpiresIn: "",
  tiltifyTokenCreatedAt: "",
  /**
   * Dev-only: when `true`, the app treats the user as signed in without a real
   * OAuth session. Populated by the Developer Settings modal so you can act as
   * any Tiltify user locally to preview campaigns. Only honored when the app
   * is running in a dev environment.
   */
  tiltifyDevImpersonation: false as boolean,
};

export type TiltifyConfig = typeof defaultTiltifyConfig;

/** Hook that reads from the shared TiltifyConfigContext. */
export default function useTiltifyConfig() {
  return useContext(TiltifyConfigContext);
}

/** Returns the state and setters needed to back TiltifyConfigContext — used by TiltifyConfigProvider. */
export function useTiltifyConfigState() {
  const [config, setConfig] = useState<TiltifyConfig>(() =>
    restoreFromLocalStorage(defaultTiltifyConfig),
  );

  const setField = useCallback(<K extends keyof TiltifyConfig>(key: K, value: TiltifyConfig[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    localStorage.setItem(key, String(value));
  }, []);

  const setFromEvent = useCallback((e: ChangeEventLike) => {
    const { name, value } = e.target;
    setConfig((prev) => ({ ...prev, [name]: value }));
    localStorage.setItem(name, value);
  }, []);

  const clearField = useCallback((key: keyof TiltifyConfig) => {
    setConfig((prev) => ({ ...prev, [key]: defaultTiltifyConfig[key] }));
    localStorage.removeItem(key);
  }, []);

  return { config, setField, setFromEvent, clearField };
}
